/**
 * PATH: packages/email-hub/src/feature/lib/orgMailboxHealth.ts
 * FILE_ID: EMAILHUB-FEATURE-LIB-ORGMAILBOXHEALTH-001
 * PURPOSE: Pure selectors for the admin org-wide mailbox health panel —
 *          classify each mailbox (dead / error / stale / paused / ok) and
 *          order the list worst-first so an admin sees problems instantly.
 * WHY: Operator ask 2026-06-10: "there should be an admin dashboard where
 *      the admin gets these notifications … the users' mailbox should not
 *      be getting these notifications". The reconnect banner is now scoped
 *      to the user's OWN mailboxes; this module powers the admin-only
 *      org-wide view (OrgMailboxHealthPanel) fed by
 *      /api/emails/sync/status/?scope=org.
 * FLOW:
 *   ┌──────────────────┐   ┌──────────────────────┐   ┌─────────────────┐
 *   │ mailboxes[]      │ → │ classifyMailboxHealth│ → │ worst-first list │
 *   │ (scope=org)      │   │ + sortByHealth       │   │ for the panel    │
 *   └──────────────────┘   └──────────────────────┘   └─────────────────┘
 * RELATED:
 *   - ../components/OrgMailboxHealthPanel.tsx: the rendering surface
 *   - ./reconnectMailboxes.ts: the user-scope equivalent (banner)
 */
import type { EmailMailboxSyncStatus } from '../hooks/useEmails.types'

export type MailboxHealth = 'dead' | 'error' | 'stale' | 'paused' | 'ok'

/** A mailbox whose last successful sync is older than this is "stale" —
 *  the periodic beat runs every 15min, so 2h of silence means several
 *  consecutive cycles failed or skipped it. */
export const STALE_THRESHOLD_SECONDS = 2 * 60 * 60

/** Pure I→O: one mailbox row → health bucket.
 *  Priority: paused (deliberate) is reported as its own bucket so admins
 *  don't chase mailboxes a user intentionally turned off; dead means the
 *  backend disabled it (reconnect required); error means active but the
 *  last run recorded a failure; stale means no recent successful sync. */
export function classifyMailboxHealth(
  m: EmailMailboxSyncStatus | null | undefined,
): MailboxHealth {
  if (!m) return 'ok'
  if (m.sync_enabled === false) return 'paused'
  if (m.is_active === false) return 'dead'
  const err = (m.last_sync_error || '').toLowerCase()
  if (err.startsWith('reconnect_required')) return 'dead'
  // A tokenless mailbox (no usable OAuth credentials) can never sync until
  // reconnected — it's effectively dead even though is_active stayed true and
  // no sync ever ran to record an error. BUG-2026-06-24-EMAIL-TOKENLESS-ZOMBIE.
  if (m.needs_reconnect === true) return 'dead'
  if (err) return 'error'
  const age = m.last_sync_age_seconds
  if (age == null || age > STALE_THRESHOLD_SECONDS) return 'stale'
  return 'ok'
}

const HEALTH_RANK: Record<MailboxHealth, number> = {
  dead: 0,
  error: 1,
  stale: 2,
  paused: 3,
  ok: 4,
}

/** Pure I→O: worst-first ordering — dead, then errored, then stale, then
 *  paused, then healthy; ties broken by stalest-first, then address.
 *  Returns a fresh array, never mutates the input. */
export function sortMailboxesByHealth(
  mailboxes: EmailMailboxSyncStatus[] | undefined | null,
): EmailMailboxSyncStatus[] {
  if (!Array.isArray(mailboxes) || mailboxes.length === 0) return []
  return mailboxes
    .filter(Boolean)
    .slice()
    .sort((a, b) => {
      const rank = HEALTH_RANK[classifyMailboxHealth(a)] - HEALTH_RANK[classifyMailboxHealth(b)]
      if (rank !== 0) return rank
      const ageA = a.last_sync_age_seconds ?? Number.POSITIVE_INFINITY
      const ageB = b.last_sync_age_seconds ?? Number.POSITIVE_INFINITY
      if (ageA !== ageB) return ageB - ageA
      return (a.email_address || '').localeCompare(b.email_address || '')
    })
}

/** Pure I→O: roll-up counts for the panel header chip. */
export function summariseMailboxHealth(
  mailboxes: EmailMailboxSyncStatus[] | undefined | null,
): Record<MailboxHealth, number> {
  const out: Record<MailboxHealth, number> = {
    dead: 0, error: 0, stale: 0, paused: 0, ok: 0,
  }
  for (const m of mailboxes ?? []) {
    out[classifyMailboxHealth(m)] += 1
  }
  return out
}
